import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Activity, TrendingUp, AlertTriangle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { KPICards } from "@/components/dashboard/KPICards";
import { CashFlowChart } from "@/components/dashboard/CashFlowChart";
import { PortfolioHealthChart } from "@/components/dashboard/PortfolioHealthChart";
import { PatrimonyEvolutionChart } from "@/components/dashboard/PatrimonyEvolutionChart";
import { DailyReport } from "@/components/dashboard/DailyReport";
import { LoanAuditLog } from "@/components/dashboard/LoanAuditLog";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { isPast } from "date-fns";
import { useSubscriptionLimits } from "@/hooks/useSubscriptionLimits";
import { toast } from "sonner";
import { useCurrency } from "@/hooks/useUserCurrency";
import {
  aggregatePayments,
  loanedInRange,
  monthlySeries,
  periodRange,
  totalOutstanding,
  PERIOD_OPTIONS,
  type FinanceLoan,
  type FinancePayment,
  type PeriodKey,
} from "@/lib/finance";

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [period, setPeriod] = useState<PeriodKey>("month");
  const { isPro, isLoading: subLoading } = useSubscriptionLimits(user?.id);
  const { format } = useCurrency();

  useEffect(() => {
    if (!user || subLoading) return;
    if (!isPro) {
      toast.error("Acesso restrito ao plano PRO. Assine para acessar o Dashboard.");
      navigate("/subscription");
    }
  }, [user, isPro, subLoading, navigate]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error || !session) {
          navigate("/auth");
          return;
        }
        setUser(session.user);
      } catch (err) {
        console.error('Session check error:', err);
        navigate("/auth");
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        navigate("/auth");
        return;
      }
      setUser(session.user);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const { data: loansData = [] } = useQuery({
    queryKey: ["loans", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("loans")
        .select(`
          *,
          clients!loans_client_id_fkey(name, phone, cpf)
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const { data: paymentsData = [] } = useQuery({
    queryKey: ["payments", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("loan_payments")
        .select("*")
        .eq("user_id", user.id);

      if (error) throw error;
      return data;
    },
  });

  const { data: clientsData = [] } = useQuery({
    queryKey: ["clients-dashboard", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, created_at")
        .eq("user_id", user.id);
      if (error) throw error;
      return data;
    },
  });

  const parseLocalDate = (s: string) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const loans = loansData.map((loan) => ({
    id: loan.id,
    clientId: loan.client_id,
    clientName: loan.clients?.name ?? "Cliente não encontrado",
    amount: Number(loan.amount),
    dueDate: parseLocalDate(loan.due_date),
    loanDate: parseLocalDate(loan.loan_date),
    status: loan.status as "paid" | "active" | "overdue",
    interestRate: Number(loan.interest_rate),
    interestType: loan.interest_type as "monthly" | "fixed" | "daily",
    installments: loan.installments || 1,
    paidInstallments: loan.paid_installments || 0,
    installmentValues: (loan as any).installment_values ?? null,
    phone: loan.clients?.phone ?? null,
    cpf: loan.clients?.cpf ?? null,
    updatedAt: new Date(loan.updated_at),
  }));

  const payments: FinancePayment[] = paymentsData.map((payment) => ({
    id: payment.id,
    loanId: payment.loan_id,
    amount: Number(payment.amount),
    interestAmount: Number(payment.interest_amount),
    principalAmount: Number(payment.principal_amount),
    penaltyAmount: Number((payment as any).penalty_amount) || 0,
    paidAt: new Date(payment.paid_at),
    isSettlement: Boolean((payment as any).is_settlement),
  }));

  const financeLoans: FinanceLoan[] = loans;

  const { start, end, months } = useMemo(() => periodRange(period), [period]);

  const totals = useMemo(
    () => aggregatePayments(payments, financeLoans, start, end),
    [paymentsData, loansData, start, end],
  );

  const loaned = useMemo(
    () => loanedInRange(financeLoans, start, end),
    [loansData, start, end],
  );

  const outstanding = useMemo(
    () => totalOutstanding(financeLoans, payments),
    [loansData, paymentsData],
  );

  const series = useMemo(
    () => monthlySeries(financeLoans, payments, period === "all" ? 12 : Math.max(months, 6)),
    [loansData, paymentsData, period, months],
  );

  const net = Number((totals.received - loaned).toFixed(2));

  // Rentabilidade: lucro real ÷ saldo médio na rua no período
  const periodPoints = series.slice(-months);
  const avgOutstanding =
    periodPoints.length > 0
      ? periodPoints.reduce((s, p) => s + p.outstanding, 0) / periodPoints.length
      : outstanding;
  const profitability = avgOutstanding > 0 ? (totals.profit / avgOutstanding) * 100 : 0;

  const activeLoans = loans.filter((l) => l.status === "active" || l.status === "overdue");
  const activeClients = new Set(activeLoans.map((l) => l.clientId)).size;

  // Portfolio health
  const onTimeLoans = loans.filter((l) => l.status === "active" && !isPast(l.dueDate)).length;
  const overdueLoans = loans.filter((l) => l.status === "overdue" || (l.status === "active" && isPast(l.dueDate))).length;
  const criticalLoans = loans.filter((l) => {
    if (l.status !== "overdue" && !(l.status === "active" && isPast(l.dueDate))) return false;
    const daysPast = Math.floor((new Date().getTime() - l.dueDate.getTime()) / (1000 * 60 * 60 * 24));
    return daysPast > 30;
  }).length;

  const periodLabel = period === "month" ? "no mês" : "no período";

  if (!user) return null;

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="animate-fade-in">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="shrink-0 -ml-2"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-2xl lg:text-3xl font-display font-semibold tracking-tight">
                  Dashboard
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              onClick={() => navigate("/central-inteligente")}
              className="gap-2 self-start"
            >
              <Sparkles className="h-4 w-4" />
              <span>Central Inteligente</span>
            </Button>

            {/* Quick stats strip */}
            <div className="grid grid-cols-3 gap-2 lg:gap-3 lg:min-w-[380px]">
              <div className="rounded-xl glass-subtle px-3 py-2.5">
                <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                  <Activity className="h-3 w-3 text-primary" />
                  Na rua
                </div>
                <div className="text-sm lg:text-base font-semibold tabular text-foreground">
                  {format(outstanding, { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div className="rounded-xl glass-subtle px-3 py-2.5">
                <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                  <TrendingUp className="h-3 w-3 text-success" />
                  Lucro real
                </div>
                <div className="text-sm lg:text-base font-semibold tabular text-success">
                  {format(totals.profit, { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div className="rounded-xl glass-subtle px-3 py-2.5">
                <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                  <AlertTriangle className="h-3 w-3 text-destructive" />
                  Vencidos
                </div>
                <div className="text-sm lg:text-base font-semibold tabular text-destructive">
                  {overdueLoans}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtro de período */}
        <div className="animate-fade-in -mx-1 overflow-x-auto pb-1">
          <div className="flex items-center gap-2 px-1">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setPeriod(opt.key)}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  period === opt.key
                    ? "bg-primary text-primary-foreground"
                    : "glass-subtle text-muted-foreground hover:text-foreground"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards */}
        <div className="animate-scale-in">
          <KPICards
            outstanding={outstanding}
            activeClients={activeClients}
            received={totals.received}
            principal={totals.principal}
            interest={totals.interest}
            penalty={totals.penalty}
            profit={totals.profit}
            loaned={loaned}
            net={net}
            profitability={profitability}
            periodLabel={periodLabel}
          />
        </div>

        {/* Relatório do Dia */}
        <div className="animate-fade-in">
          <DailyReport
            loans={loans}
            payments={payments.map((p) => ({
              id: p.id,
              loanId: p.loanId,
              amount: p.amount,
              interestAmount: p.interestAmount,
              paidAt: p.paidAt,
            }))}
            clients={clientsData.map((c: any) => ({
              id: c.id,
              createdAt: new Date(c.created_at),
            }))}
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 animate-fade-in">
          <CashFlowChart data={series} />
          <PortfolioHealthChart 
            onTimeCount={onTimeLoans}
            overdueCount={overdueLoans - criticalLoans}
            criticalCount={criticalLoans}
          />
        </div>

        <div className="animate-fade-in">
          <PatrimonyEvolutionChart data={series} />
        </div>

        {user && (
          <div className="animate-fade-in">
            <LoanAuditLog userId={user.id} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
